<template>
  <div class="panel">
    <div class="section">
      <div class="title">{{ $t('editor.design.colorPalettes') }}</div>
      <div class="items">
        <div
          v-for="palette in palettes"
          :key="palette.name"
          class="item"
          :class="{ selected: palette.name === selectedPalette }"
        >
          <div class="item-title">{{ palette.name }}</div>
          <div class="colors">
            <div v-for="(color, index) in palette.variables" :key="index" class="color">
              <div class="circle" :style="'background-color:' + color.value" />
              <div class="code">{{ color.value }}</div>
            </div>
          </div>
          <div v-if="selectedPalette === palette.name" class="selected footer">
            <check class="icon" />
            {{ $t('words.selected') }}
          </div>
          <div
            v-else
            class="secondary-button"
            @click="
              () => {
                selectedPalette = palette.name;
              }
            "
          >
            {{ $t('editor.design.select') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import check from '@/assets/icons/checked.svg';
import { mapGetters } from 'vuex';

export default {
  name: 'ColorPalette',
  components: {
    check,
  },
  props: {
    resume: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      palettes: [],
      selectedPalette: '',
    };
  },
  computed: {
    ...mapGetters(['templates']),
  },
  watch: {
    selectedPalette(name) {
      if (!name) {
        return;
      }
      const palette = this.palettes.find(p => p.name === name);
      const variables = [
        { name: 'color1', value: palette.variables[0].value, type: 'color' },
        { name: 'color2', value: palette.variables[1].value, type: 'color' },
        { name: 'color3', value: palette.variables[2].value, type: 'color' },
      ];
      this.$store
        .dispatch('resumes.template.edit', {
          resumeId: this.resume.id,
          template: { variables },
        })
        .then(() => {
          this.$store.dispatch('resume.template.setColor', variables);
        });
    },
  },
  created() {
    this.palettes = this.resume.template.palettes;
    const templatePalette = this.palettes.find(
      p =>
        p.variables[0].value === this.resume.template.variables[0].value &&
        p.variables[1].value === this.resume.template.variables[1].value &&
        p.variables[2].value === this.resume.template.variables[2].value
    );
    if (templatePalette) {
      this.selectedPalette = templatePalette.name;
    } else {
      this.selectedPalette = this.palettes[0].name;
    }
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

$title-height: 32px;
$light-grey-color: grey;

.panel {
  border-left: solid 1px #d0d0d0;
  left: 200px !important;
  height: calc(100% - 30px);
  overflow-y: scroll;
  overflow-x: initial;
  $title-height: 30px;
  padding: 8px 24px;
  border-right: none;
  width: 320px !important;
  min-width: 260px;
  max-width: 700px;

  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-right: solid 1px #d0d0d0;
    &:first-child {
      .title {
        border-top: none;
      }
    }
    .title {
      height: $title-height;
      line-height: $title-height;
      text-transform: uppercase;
      font-weight: bold;
      color: colors.$darkest-grey-color;
      border-bottom: solid 1px #d0d0d0;
      font-size: 14px;
      box-sizing: border-box;
      user-select: none;
      width: 280px;
      letter-spacing: 1.3px;
    }

    .items {
      height: calc(100vh - #{app.$topbar-height} - #{$title-height});
      overflow: scroll;
      width: 100%;
      &:focus {
        outline: none;
      }

      .item {
        font-size: 14px;
        color: #7d7d7d;
        padding: 11px 0;
        margin: 0 auto;
        box-sizing: border-box;
        user-select: none;
        width: 280px;
        border-top: solid 1px #d0d0d0;
        cursor: default;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        &:focus {
          outline: none;
        }
        &:first-child {
          margin-top: 11px;
          border-top: solid 1px transparent;
        }

        .item-title {
          box-sizing: border-box;
          padding: 0 10px 0 10px;
          color: colors.$dark-text-color;
          background-color: colors.$white-color;
          border-radius: 2px;
          height: 26px;
          width: 189px;
          display: flex;
          align-items: center;
          font-size: 12px;
          font-weight: 600;
        }

        &.selected {
          background: colors.$light-blue-color;
          color: colors.$white-color;
          width: 100%;
          padding: 11px 22px;

          & + .item {
            border-top: solid 1px transparent;
          }

          .footer {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 16px;
            margin-top: 14px;
            width: 100%;
            font-size: 11px;
            text-transform: uppercase;
            font-weight: 600;
            .icon {
              width: 11px;
              height: 11px;
              margin-right: 7px;
            }
          }
        }
        .colors {
          box-sizing: border-box;
          display: flex;
          justify-content: space-between;
          width: 189px;
          .color {
            text-align: center;
            margin-top: 14px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            .circle {
              height: 41px;
              width: 41px;
              border-radius: 50%;
              margin-bottom: 4px;
              border: 1px solid colors.$white-color;
            }
            .code {
              font-size: 9px;
              text-transform: uppercase;
            }
          }
        }
        .secondary-button {
          margin-top: 14px;
          width: 189px;
          font-weight: 600;
          transition: box-shadow 0.3s;
          box-sizing: border-box;
          &:hover {
            box-shadow: 0 0 0 4px colors.$hover-create-button-shadow;
          }
        }
      }
    }
  }
}
</style>
